import { consumeDeepLink, openMessageLink, parseDeepLink, type DeepLink } from '../deeplink'
import { fmtCoords, relativeTime } from '../format'
import {
  canonical,
  distanceKm,
  GeoError,
  geoPermission,
  getCurrentPosition,
  SAME_PLACE_KM,
  type Coords,
  type GeoErrorKind,
  type GeoPermission,
} from '../geolocation'
import { onNativeNavigate } from '../native'
import { whileVisible } from '../visibility'
import { requestAlert } from './alertLink.svelte'
import { setView } from './layout.svelte'
import {
  location,
  placeName,
  restoreShown,
  select,
  type LoadOutcome,
  type SelectedPoint,
} from './location.svelte'
import { consumePlacesLink, getPlace, places, unsay, type SavedPlace } from './places.svelte'
import { prefs, setLastFix } from './prefs.svelte'

/**
 * My location: what the app opens to, and keeping it true afterwards (Phase 9
 * plan §5).
 *
 * The boot shows the last fix at once and asks for a new one; a fix within
 * 1 km of what is shown is the *same point* and costs nothing, and one further
 * away moves the forecast there. Every automatic step holds the selection
 * generation it started from and gives up when it has moved, so a late fix
 * never drags the map away from something the user chose in the meantime.
 */

/** Why the boot is showing what it is showing, in the status line. */
export type HereNote =
  /** A fix is outstanding. The point on screen, if any, is the last fix. */
  | { kind: 'locating' }
  /** The fix failed; the point on screen is the last fix, taken at `at`. */
  | { kind: 'stale'; at: number }
  /** No fix could be had. `showing`: the last viewed place is on screen. */
  | { kind: 'fallback'; reason: FallbackReason; showing: boolean }
  /** A fix on return moved the forecast without moving the camera. */
  | { kind: 'moved' }
  /** A fix moved, its forecast could not be fetched, and the last fix is back. */
  | { kind: 'offline' }

export type FallbackReason = GeoErrorKind

export const here = $state({
  /** The browser's answer as last seen. Settings reads it (Stage 9.3). */
  permission: 'unknown' as GeoPermission,
  note: null as HereNote | null,
  /** The selection generation `note` is about; any later selection hides it. */
  noteFor: -1,
})

/** The note for what is on screen now, or null once anything else was chosen. */
export function hereNote(): HereNote | null {
  return here.noteFor === location.generation ? here.note : null
}

/**
 * No point, and the browser has refused to give one: the first run with
 * location blocked (round 2's 3.7). A returning visitor opens to the last
 * place viewed, so in practice this is the very first launch. Only a refusal:
 * a browser with no location at all (plain http on a LAN, jsdom) keeps the
 * ordinary empty sheet and its "Location needs https" line.
 */
export function firstRunBlocked(): boolean {
  return !location.selected && here.permission === 'denied' && hereNote()?.kind !== 'locating'
}

/**
 * Short on purpose: on a phone this is one line under the place in the panel,
 * and a sentence that wraps pushes everything under it out of view.
 */
const FALLBACK_TEXT: Record<FallbackReason, string> = {
  denied: 'Location blocked',
  insecure: 'Location needs https',
  unsupported: 'No location in this browser',
  unavailable: 'Location unavailable',
  timeout: 'Locating timed out',
}

/** The status line's words for `note`. `now` is injectable for the tests. */
export function hereNoteText(note: HereNote, now: Date = new Date()): string {
  switch (note.kind) {
    case 'locating':
      return 'Locating…'
    case 'stale':
      return `Last known location · ${relativeTime(new Date(note.at), now)}`
    case 'fallback':
      return note.showing
        ? `${FALLBACK_TEXT[note.reason]} · showing last place viewed`
        : FALLBACK_TEXT[note.reason]
    case 'moved': {
      const point = location.selected
      const name = placeName() ?? (point ? fmtCoords(point.lat, point.lon) : 'your location')
      return `Moved to ${name}`
    }
    case 'offline':
      return 'Offline: showing last known location'
  }
}

function note(value: HereNote | null): void {
  here.note = value
  here.noteFor = location.generation
}

/** A fix, a boot's or a return's, is in flight. One at a time. */
let locating = false

/**
 * What the app opens to (§5.2): a deep link, else the saved place chosen as
 * the default, else My location, else the last place viewed, else the empty
 * state. Called once, from `App`'s `onMount`.
 *
 * A `#places=` link is read here too, but it only *offers* its places; what
 * opens is decided by the steps above as if it were not there.
 */
export async function boot(): Promise<void> {
  consumePlacesLink()
  const link = consumeDeepLink()
  if (link) {
    await openLink(link)
    return
  }
  const place = getPlace(places.openTo)
  if (place) {
    await select(placePoint(place, 'restore'))
    return
  }
  await openHere()
}

/**
 * Open a link's point, and its alert if it names one. The popup is asked for
 * first: it fetches by id, not by point, so it need not wait for the forecast,
 * which offline can take the service worker's whole 8 s.
 */
export async function openLink(link: DeepLink): Promise<void> {
  // A link is about a place, so it opens onto the forecast (Phase 11 §2.2).
  setView('forecast')
  if (link.alert) requestAlert(link.alert)
  await select({ lat: link.lat, lon: link.lon, source: 'link' })
}

/**
 * A notification tapped while the app is open: the service worker focuses
 * this page and posts the link rather than reloading it (§9.6), and the iOS
 * app hands over the notification's path the same way (ios-app-plan §5.3).
 * Returns a disposer.
 */
export function watchNotificationOpens(): () => void {
  // The app's path is checked to be one on this page before it gets here;
  // the query goes through the same parser as the address bar's.
  const stopNative = onNativeNavigate((path) => {
    const link = parseDeepLink(new URL(path, 'http://page.invalid').search)
    if (link) void openLink(link)
  })
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
  if (!sw) return stopNative
  const onMessage = (e: MessageEvent) => {
    const link = openMessageLink(e.data, window.location.origin)
    if (link) void openLink(link)
  }
  sw.addEventListener('message', onMessage)
  return () => {
    stopNative()
    sw.removeEventListener('message', onMessage)
  }
}

function placePoint(place: SavedPlace, source: 'restore' | 'search'): SelectedPoint {
  // `label` is the NWS name, not the saved one: it is what `lastLocation`
  // stores, and the headers find the saved name through `place`.
  return { lat: place.lat, lon: place.lon, label: place.label, zip: place.zip, source, place: place.id }
}

/**
 * Switch to a saved place (§6.4): one tap in the phone's search panel or the
 * desktop chips. The camera flies, as it does for a searched ZIP.
 */
export async function openPlace(place: SavedPlace): Promise<LoadOutcome> {
  unsay()
  return select(placePoint(place, 'search'))
}

/**
 * Switch to My location: the locate button's path, a fix and then the same
 * 1 km rule as the boot, with the camera flying. Returns a sentence for the
 * caller to show when no fix could be had, or null. `onFix` runs once there
 * is a fix and before its forecast is asked for.
 */
export async function locate(onFix?: () => void): Promise<string | null> {
  try {
    const fix = await getCurrentPosition()
    // Only now: a panel that closed before the fix failed would take the
    // error line with it.
    onFix?.()
    unsay()
    // Through `applyFix`, so a phone that has not moved lands on the very
    // point it opened to, and the fix is remembered for the next launch.
    await applyFix(fix, 'geo')
    return null
  } catch (e) {
    if (e instanceof GeoError && e.kind === 'denied') here.permission = 'denied'
    return e instanceof GeoError
      ? e.message
      : `Could not locate you: ${e instanceof Error ? e.message : String(e)}`
  }
}

async function openHere(): Promise<void> {
  const permission = await geoPermission()
  here.permission = permission
  if (permission === 'denied' || permission === 'insecure' || permission === 'unsupported') {
    fallback(permission)
    return
  }

  // The last fix, at once, rather than a map of the whole country for up to
  // the 10 s timeout. It is labelled as what it is until the fix confirms it.
  const last = prefs.lastFix
  const shown = last
    ? select({ lat: last.lat, lon: last.lon, source: 'restore', place: 'here' })
    : null
  note({ kind: 'locating' })
  const generation = location.generation

  locating = true
  let fix: Coords
  try {
    fix = await getCurrentPosition() // Prompts when the state is `prompt`.
  } catch (e) {
    const kind: FallbackReason = e instanceof GeoError ? e.kind : 'unavailable'
    if (kind === 'denied') here.permission = 'denied'
    if (location.generation !== generation) return
    if (last) note({ kind: 'stale', at: last.at })
    else fallback(kind)
    return
  } finally {
    locating = false
  }
  here.permission = 'granted'
  // Somebody tapped, searched or followed the locate button while the prompt
  // was up. What they chose wins.
  if (location.generation !== generation) return
  await applyFix(fix, 'here', shown)
}

function fallback(reason: FallbackReason): void {
  const last = prefs.lastLocation
  if (last) void select({ ...last, source: 'restore' })
  note({ kind: 'fallback', reason, showing: Boolean(last) })
}

/**
 * Show the forecast for a fix.
 *
 * Within 1 km of the point already shown as My location, the fix *is* that
 * point (§5.3): an automatic fix changes nothing and requests nothing, and the
 * locate button re-selects the same coordinates so the camera still comes
 * back to it. That is what makes an unmoved phone's launch hit the service
 * worker's saved forecast offline: same point, same URL.
 *
 * An automatic fix whose forecast cannot be fetched (offline, having
 * travelled) puts the previous point back (§5.4), because the failed load
 * kept that point's bundle on screen. A locate button press keeps the
 * ordinary rule: the new point, the old bundle and an error line.
 *
 * `shownLoad` is the placeholder's own load, still in flight at boot. A moved
 * fix waits for it: a new selection aborts the load before it, and a phone
 * with a cached OS fix answers faster than the service worker does, so
 * without the wait the offline launch that most needs the saved forecast
 * would have none to fall back to.
 */
export async function applyFix(
  fix: Coords,
  source: 'here' | 'here-refresh' | 'geo',
  shownLoad?: Promise<unknown> | null,
): Promise<void> {
  here.permission = 'granted'
  const shown = location.selected
  const point = { lat: canonical(fix.lat), lon: canonical(fix.lon) }
  const anchor =
    shown?.place === 'here' && distanceKm(shown, point) < SAME_PLACE_KM
      ? { lat: shown.lat, lon: shown.lon }
      : null
  const at = Date.now()

  if (anchor && source !== 'geo') {
    setLastFix({ ...anchor, at })
    note(null)
    return
  }

  if (shownLoad) {
    const generation = location.generation
    await shownLoad
    if (location.generation !== generation) return
  }

  const target = anchor ?? point
  const before: SelectedPoint | null = shown ? { ...shown } : null
  const pending = select({ ...target, source, place: 'here' })
  const generation = location.generation
  const outcome = await pending
  if (location.generation !== generation) return

  if (outcome === 'unreachable') {
    if (source !== 'geo' && before?.place === 'here' && restoreShown(before)) {
      note({ kind: 'offline' })
    }
    // Not recorded as the last fix: its forecast is not saved anywhere, and
    // the next launch offline is better served by the one that is.
    return
  }
  // Loaded, or the server answered for the point (outside NWS coverage, say):
  // either way the phone was there.
  setLastFix({ ...target, at })
  note(source === 'here-refresh' ? { kind: 'moved' } : null)
}

/**
 * How old the last fix must be before a return asks again. A phone picked up
 * a few times an hour has not usually gone far, and a forecast cell is 2.5 km.
 */
export const RECHECK_AFTER_MS = 15 * 60_000

/**
 * On return, check the phone is still where the forecast says (§5.5). Only
 * when the point shown *is* My location, only when permission is already
 * granted, so a return never prompts, and only when the last fix is 15
 * minutes old. A move updates the marker and the forecast under
 * `here-refresh`, which `MapView` does not follow with the camera.
 */
export async function recheckHere(): Promise<void> {
  const shown = location.selected
  if (!shown || shown.place !== 'here' || locating || location.status === 'loading') return
  const last = prefs.lastFix
  if (last && Date.now() - last.at < RECHECK_AFTER_MS) return
  const permission = await geoPermission()
  here.permission = permission
  if (permission !== 'granted') return

  const generation = location.generation
  locating = true
  try {
    const fix = await getCurrentPosition()
    if (location.generation !== generation) return
    await applyFix(fix, 'here-refresh')
  } catch {
    // The point on screen stays. Nothing was asked of the user, so nothing is
    // said to them either; the next return tries again.
  } finally {
    locating = false
  }
}

/** How often a visible page considers `recheckHere`. The check is free. */
const RECHECK_TICK_MS = 60_000

/** Start the return re-check. Returns a disposer. */
export function startHereRecheck(): () => void {
  return whileVisible(() => void recheckHere(), RECHECK_TICK_MS)
}

/** Tests only: forget an in-flight fix left behind by a test that timed out. */
export function _resetHere(): void {
  locating = false
  here.permission = 'unknown'
  here.note = null
  here.noteFor = -1
}
